
import React, { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { StartupAccount } from "@/types/startup";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Building2, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Eye,
  MessageSquare,
  AlertTriangle 
} from "lucide-react";
import StartupApplicationDetails from "./startups/StartupApplicationDetails";
import StartupReports from "./startups/StartupReports";
import StartupFeedback from "./startups/StartupFeedback";

const AdminStartups = () => {
  const [applications, setApplications] = useState<StartupAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState<StartupAccount | null>(null);
  const [activeTab, setActiveTab] = useState("applications");

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const { data, error } = await supabase
        .from("startup_accounts")
        .select(`
          *,
          profiles!startup_accounts_user_id_fkey (
            full_name,
            username,
            avatar_url
          )
        `)
        .order("submitted_at", { ascending: false });

      if (error) throw error;
      setApplications(data || []);
    } catch (error) {
      console.error("Error fetching applications:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateApplicationStatus = async (applicationId: string, status: 'approved' | 'rejected') => {
    try {
      const { error } = await supabase
        .from("startup_accounts")
        .update({ 
          status,
          approved_at: status === 'approved' ? new Date().toISOString() : null,
          approved_by: status === 'approved' ? (await supabase.auth.getUser()).data.user?.id : null
        })
        .eq("id", applicationId);

      if (error) throw error;
      
      await fetchApplications();
      setSelectedApplication(null);
    } catch (error) {
      console.error("Error updating application status:", error);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="text-yellow-600"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      case 'approved':
        return <Badge variant="outline" className="text-green-600"><CheckCircle className="h-3 w-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge variant="outline" className="text-red-600"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const pendingApplications = applications.filter(app => app.status === 'pending');
  const approvedApplications = applications.filter(app => app.status === 'approved');
  const rejectedApplications = applications.filter(app => app.status === 'rejected');

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Startup Management</h2>
        <div className="flex gap-4 text-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            <Clock className="h-4 w-4" />
            {pendingApplications.length} Pending
          </span>
          <span className="flex items-center gap-1">
            <CheckCircle className="h-4 w-4" />
            {approvedApplications.length} Approved
          </span>
          <span className="flex items-center gap-1">
            <XCircle className="h-4 w-4" />
            {rejectedApplications.length} Rejected
          </span>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="applications">Applications</TabsTrigger>
          <TabsTrigger value="reports">Reports</TabsTrigger>
          <TabsTrigger value="feedback">Feedback</TabsTrigger>
        </TabsList>

        <TabsContent value="applications" className="space-y-4">
          <div className="grid gap-4">
            {applications.map((application) => (
              <Card key={application.id}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Building2 className="h-5 w-5" />
                        {application.startup_name}
                      </CardTitle>
                      <CardDescription>
                        Submitted on {new Date(application.submitted_at).toLocaleDateString()}
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(application.status)}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedApplication(application)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {application.bio}
                  </p>
                  {application.category_tags && application.category_tags.length > 0 && (
                    <div className="flex gap-2 mt-2">
                      {application.category_tags.map((tag) => (
                        <Badge key={tag} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="reports">
          <StartupReports />
        </TabsContent>

        <TabsContent value="feedback">
          <StartupFeedback />
        </TabsContent>
      </Tabs>

      {selectedApplication && (
        <StartupApplicationDetails
          application={selectedApplication}
          onClose={() => setSelectedApplication(null)}
          onUpdateStatus={updateApplicationStatus}
        />
      )}
    </div>
  );
};

export default AdminStartups;
